import { useState, useCallback, useEffect, useMemo } from "react";
import { usePfmPostAnalyticsApi } from "../api/use-pfm-post-analytics.api";
import { ContentLog } from "../types/dashboard";
import { BrandDetails } from "../types/add-brand-types";
import {
  PostAnalyticsData,
  PlatformAnalytics,
  normalizePlatformId,
  rawNum,
  rawNested,
} from "../types/post-analytics";

const usePostAnalytics = (item: ContentLog, brandDetails: BrandDetails) => {
  const { getPostAnalytics } = usePfmPostAnalyticsApi();
  const [data, setData] = useState<PostAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activePlatform, setActivePlatform] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!item.post_id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const result = await getPostAnalytics(item.post_id);
    setData(result ?? null);
    if (result?.platforms?.length) {
      setActivePlatform(result.platforms[0].platform);
    }
    setLoading(false);
  }, [item.post_id, getPostAnalytics]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const activePlatformData: PlatformAnalytics | null = useMemo(() => {
    if (!data || !activePlatform) return null;
    return (
      data.platforms.find(
        (p) =>
          normalizePlatformId(p.platform) ===
          normalizePlatformId(activePlatform)
      ) ?? null
    );
  }, [data, activePlatform]);

  const mediaPreviewUrls = useMemo(() => {
    return (item.content?.media ?? []).map((m) => m.url);
  }, [item.content?.media]);

  const mediaMetadata = useMemo(() => {
    return (item.content?.media ?? []).map((m) => ({
      url: m.url,
      type: m.type as "image" | "video",
      duration: undefined as number | undefined,
    }));
  }, [item.content?.media]);

  const accountsSource =
    brandDetails.pfm_social_profile_ids ?? brandDetails.social_profile_ids;

  const getPreviewAccount = useCallback(
    (platformId: string) =>
      accountsSource?.accounts?.find(
        (a: {
          platform: string;
          username?: string;
          displayName?: string;
          profilePicture?: string;
        }) => a.platform?.toLowerCase() === platformId.toLowerCase()
      ),
    [accountsSource]
  );

  const metrics = activePlatformData?.metrics ?? {
    views: 0,
    likes: 0,
    comments: 0,
    shares: 0,
  };

  const raw = (activePlatformData?.raw_metrics ?? {}) as Record<
    string,
    unknown
  >;

  const postBody = item.content?.content ?? "";
  const postTitle = item.content?.title ?? "";
  const subreddits = item.content?.subreddits;

  const twitterBookmarks = rawNested(raw, "public_metrics", "bookmark_count");
  const instagramSaved = rawNum(raw, "saved");
  const facebookReactionsTotal = rawNum(raw, "reactions_total");
  const linkedInLikeCount = rawNum(raw, "likeCount");
  const linkedInCommentCount = rawNum(raw, "commentCount");
  const linkedInShareCount = rawNum(raw, "shareCount");

  return {
    data,
    loading,
    activePlatform,
    setActivePlatform,
    activePlatformData,
    mediaPreviewUrls,
    mediaMetadata,
    getPreviewAccount,
    metrics,
    raw,
    postBody,
    postTitle,
    subreddits,
    twitterBookmarks,
    instagramSaved,
    facebookReactionsTotal,
    linkedInLikeCount,
    linkedInCommentCount,
    linkedInShareCount,
  };
};

export default usePostAnalytics;
